import { uninstallBootstrapHandlers } from './bootstrapHandlers';
import {
  BROWSER_ROLE_NAME,
  setGlobalContext,
  clearGlobalContext,
} from './context';
import { logger, setSink } from './logger';
import { stripQuery } from './scrub';

/**
 * Application Insights browser SDK lifecycle.
 *
 * The SDK is behind a dynamic import() so it lands in its own chunk, never blocks first paint,
 * and is not fetched at all when there is no connection string. That single seam is also what
 * keeps it out of jsdom during tests.
 */

// Feature flag name, served through SiteSettings.Features from the API's FeaturesController.
// The flag is declared as `FeatureManagement:BrowserTelemetry` in the API's appsettings (or as
// an Azure App Configuration feature flag labelled with the environment name).
export const TELEMETRY_FEATURE_FLAG = 'BrowserTelemetry';

/**
 * Case-insensitive flag lookup.
 *
 * The name declared in configuration is PascalCase, but the API serialises with
 * AddNewtonsoftJson(UseCamelCasing(true)), which camelCases dictionary keys too -- so the
 * value that actually reaches the browser is `features.browserTelemetry`. Matching either
 * way means the gate does not silently fail if that serializer setting ever changes.
 */
export function readFeatureFlag(features, name) {
  if (!features || typeof features !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(features, name))
    return features[name];

  const wanted = name.toLowerCase();
  const key = Object.keys(features).find((k) => k.toLowerCase() === wanted);
  return key === undefined ? undefined : features[key];
}

// On unless the flag is explicitly false. A missing flag has to mean on, because
// SettingsController leaves Features empty when its call to the API fails -- with the opposite
// default, a flaky API would silently disable telemetry exactly when it is most wanted.
// Turning it off is therefore an explicit act: set the flag to false.
const isEnabledByFlag = (features) =>
  readFeatureFlag(features, TELEMETRY_FEATURE_FLAG) !== false;

let appInsights = null;
let initializedWith = null;
let sdkLoader = () => import('@microsoft/applicationinsights-web');

/** Test hook: inject a fake SDK so jsdom never loads the real one. */
export function __setSdkLoaderForTests(loader) {
  sdkLoader = loader;
}

export function isInitialized() {
  return appInsights !== null;
}

export function getAppInsights() {
  return appInsights;
}

function hostOf(url) {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

/**
 * Stamps the dimensions that let browser items join with the server's Serilog output, and
 * strips anything that should not leave the browser.
 */
function buildTelemetryInitializer(settings) {
  return (item) => {
    item.tags = item.tags || {};
    // The backend hardcodes cloud_RoleName per app because it runs on on-premises IIS. Do the
    // same here, and deliberately NOT "Unity Web": a distinct role keeps the existing detectors
    // from matching browser noise as if it were server output, and gives Application Map a
    // fourth node.
    item.tags['ai.cloud.role'] = BROWSER_ROLE_NAME;
    if (settings.appVersion) {
      item.tags['ai.application.ver'] = settings.appVersion;
    }

    item.data = item.data || {};
    item.data.ApplicationName = BROWSER_ROLE_NAME;
    if (settings.environmentName) {
      item.data.EnvironmentName = settings.environmentName;
    }

    // Replay of pre-init buffered events: keep the original timestamp rather than the
    // moment the SDK happened to become ready.
    if (typeof item.data.LoggedAt === 'string') {
      item.time = item.data.LoggedAt;
    }

    // Query strings carry record ids and, on the search route, user search terms.
    if (item.baseData) {
      if (typeof item.baseData.uri === 'string') {
        item.baseData.uri = stripQuery(item.baseData.uri);
      }
      if (typeof item.baseData.refUri === 'string') {
        item.baseData.refUri = stripQuery(item.baseData.refUri);
      }
    }

    return true;
  };
}

function buildConfig(settings, DistributedTracingModes) {
  const apiHost = hostOf(settings.unityApiEndpoint);

  return {
    connectionString: settings.telemetryConnectionString,

    // ASP.NET Core reads W3C traceparent natively, so the browser's trace id becomes the
    // server request's operation_Id. That join is the entire point of this work.
    distributedTracingMode: DistributedTracingModes.W3C,

    // Required: the API is a different host in every environment, and the SDK omits
    // correlation headers cross-origin by default.
    enableCorsCorrelation: true,

    // This allowlist is a correctness requirement, not a tidy-up. With CORS correlation on and
    // no allowlist the SDK injects traceparent/Request-Id into EVERY cross-origin call, which
    // changes the CORS preflight. providers/authorizeNet.js posts straight to api.authorize.net
    // from the browser, and TextEditor.js loads TinyMCE plugins from cdn.tiny.cloud; if those
    // hosts do not allow the headers, the preflight fails and those features break. The identity
    // host is deliberately absent until sign-in has been tested with it.
    correlationHeaderDomains: apiHost ? [apiHost] : [],

    excludeRequestFromAutoTrackingPatterns: [
      // RefreshSettingsButton polls this every 30s, per open tab. Biggest single cost lever.
      /\/settings(\?|$)/i,
      // The client-error email channel; reporting an error should not itself be telemetry.
      /\/Error$/i,
      // Our own ingestion calls.
      /applicationinsights\.azure\.com/i,
    ],

    // Defaults to false. Turning it on is what makes the un-awaited rejections in
    // providers/core/api.js and UserContext's un-caught fetches visible at all.
    enableUnhandledPromiseRejectionTracking: true,

    // Route tracking is done by usePageViews so ids collapse to {id}; the built-in version
    // names page views by raw URL and produces one name per record.
    enableAutoRouteTracking: false,

    autoTrackPageVisitTime: true,
    maxAjaxCallsPerView: 500,
    samplingPercentage: 100,

    // Left at their defaults on purpose:
    //   enableRequestHeaderTracking / enableResponseHeaderTracking = false
    //     -> enabling either ships `Authorization: Bearer <jwt>` into telemetry. Never enable.
    //   disableExceptionTracking = false -> the SDK hooks window.onerror itself, so adding our
    //     own permanent handler would double-report.
    //   loggingLevelConsole = 0 -> the SDK stays out of the console.
  };
}

/**
 * Initialises telemetry. Safe and cheap to call more than once, and safe to call with
 * nothing: a blank connection string or a disabled flag returns before the SDK is even
 * fetched. Never throws -- telemetry must not be able to stop the app booting.
 *
 * Not awaited by callers: the SDK chunk must not delay first render.
 */
export async function initTelemetry(settings) {
  try {
    if (!settings) return false;

    const connectionString = settings.telemetryConnectionString;
    if (!connectionString) {
      // The server already warns about this at startup (Program.cs), so stay quiet in
      // deployed environments and only help out locally.
      if (
        process.env.NODE_ENV !== 'production' &&
        settings.environmentName !== undefined
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          '[telemetry] No connection string in /settings; browser telemetry is off.'
        );
      }
      return false;
    }

    if (!isEnabledByFlag(settings.features)) return false;

    if (appInsights && initializedWith === connectionString) return true;
    if (appInsights) {
      // Connection string changed under us (App Configuration edit + reload path).
      appInsights.config.connectionString = connectionString;
      initializedWith = connectionString;
      return true;
    }

    const { ApplicationInsights, DistributedTracingModes } = await sdkLoader();

    const instance = new ApplicationInsights({
      config: buildConfig(settings, DistributedTracingModes),
    });
    instance.loadAppInsights();
    instance.addTelemetryInitializer(buildTelemetryInitializer(settings));

    // Hand over from the temporary handlers before the SDK installs its own, so there is
    // no window in which both are listening.
    uninstallBootstrapHandlers();

    appInsights = instance;
    initializedWith = connectionString;

    setGlobalContext({
      EnvironmentName: settings.environmentName ?? '',
      AppVersion: settings.appVersion ?? '',
    });

    // Hand the logger a sink, which also replays anything buffered before now.
    setSink({
      trackTrace: (item) => instance.trackTrace(item),
      trackException: (item) => instance.trackException(item),
      trackEvent: (item) => instance.trackEvent(item),
      trackMetric: (item) => instance.trackMetric(item),
      trackPageView: (item) => instance.trackPageView(item),
    });

    if (process.env.NODE_ENV !== 'production') {
      // window.appInsights does not exist with the npm setup; give devs a handle to flush.
      window.__unityTelemetry = instance;
    }

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV !== 'production')
      console.warn('[telemetry] init failed', error);
    return false;
  }
}

/**
 * Associates telemetry with the signed-in user. Uses the OIDC `sub` -- an opaque stable id --
 * rather than preferred_username, which is an email address.
 */
export function setTelemetryUser(user) {
  const sub = user?.profile?.sub;
  if (!sub) return;

  // setAuthenticatedUserContext rejects ids containing , ; = | or whitespace.
  const safeId = String(sub).replace(/[,;=|\s]/g, '_');
  setGlobalContext({ UserSub: safeId });
  appInsights?.setAuthenticatedUserContext(safeId, undefined, false);
}

export function clearTelemetryUser() {
  clearGlobalContext(['UserSub']);
  appInsights?.clearAuthenticatedUserContext();
}

export function flushTelemetry() {
  appInsights?.flush();
}

/**
 * Kill switch for a running session. The flag alone cannot do this: getFeatures() reads
 * localStorage and UserContext's interval only re-reads localStorage, so a flag change
 * otherwise takes effect on reload. Call this from the code path that actually re-fetches
 * /settings.
 */
export function applyTelemetryFeatureFlag(settings) {
  if (!appInsights) return;
  appInsights.config.disableTelemetry = !isEnabledByFlag(settings?.features);
}

/** Test hook. */
export function resetTelemetryForTests() {
  appInsights = null;
  initializedWith = null;
  sdkLoader = () => import('@microsoft/applicationinsights-web');
}

export { logger };
